import { inject } from '@angular/core';
import {
	CanActivateFn,
	Router,
	ActivatedRouteSnapshot,
	RouterStateSnapshot,
} from '@angular/router';
import { map, take } from 'rxjs/operators';
import { LoggedInService } from '../services/logged-in.service';
import { firstValueFrom } from 'rxjs';
import { LocalStorageService } from '../services/local-storage.service';
import { NavigationService } from '../services/navigation.service';
import { Security } from '../services/security';
import { VerifyUuidsPayload } from '@mira-avant/mtlibs';

export const isLoggedInAndRedirectToDestinationGuard: CanActivateFn = async (
	route: ActivatedRouteSnapshot,
	state: RouterStateSnapshot
) => {
	const loggedInService = inject(LoggedInService);
	const router = inject(Router);
	const localStorageService = inject(LocalStorageService);
	const nav = inject(NavigationService);
	const securityService = inject(Security);

	const isLoggedIn = await firstValueFrom(
		loggedInService.isLoggedIn$.pipe(
			take(1),
			map(isLoggedInValue => {
				return isLoggedInValue;
			})
		)
	);

	if (isLoggedIn) {
		// Proceed with redirectToDestination logic here
		const desiredDestination: string | undefined =
			localStorageService.getLoginDestination(false);

		if (desiredDestination) {
			const currentUrl = state.url;
			const isAtDestination = currentUrl === desiredDestination;

			if (!isAtDestination) {
				localStorageService.clearLoginDestination();

				const matrixParamsInUrl = nav.extractMatrixParamsFromUrl(desiredDestination);

				//verify the UUIDs this user is requesting are legitimate
				const isLegit = await securityService.verify$(
					matrixParamsInUrl as unknown as VerifyUuidsPayload
				);

				if (!isLegit) {
					console.warn(
						'Unauthorized navigation attempt to:',
						desiredDestination,
						'UUID mismatch in stored / requested URL.'
					);
				}

				isLegit
					? nav.navigateByUrl(desiredDestination)
					: router.navigate(['/', 'my', 'journey']);
				return false; // Prevent further navigation
			} else {
				localStorageService.clearLoginDestination();
				return true; // Allow navigation to continue
			}
		}
		return true; // No stored destination, allow normal navigation
	} else {
		router.navigate(['/']); // Redirect to home page if not logged in
		return false;
	}
};
